import { useId, useState, type ReactNode } from 'react';

import {
  Answer,
  Heading,
  IconWrap,
  Item,
  List,
  MinusIcon,
  Outer,
  Paragraph,
  Panel,
  PanelInner,
  PlusIcon,
  Section,
  Summary,
  Toggle,
  Question,
} from './raffle-faq-section.styles';

export type RaffleFaqItem = {
  answer: ReadonlyArray<string>;
  question: string;
};

export type RaffleFaqSectionProps = {
  className?: string;
  heading?: ReactNode;
  items: ReadonlyArray<RaffleFaqItem>;
};

function resolveSectionLabel(heading: ReactNode) {
  if (typeof heading === 'string' || typeof heading === 'number') {
    return String(heading);
  }

  return 'FAQ';
}

export function RaffleFaqSection({
  className,
  heading,
  items,
}: RaffleFaqSectionProps) {
  const baseId = useId();
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);

  if (items.length === 0) {
    return null;
  }

  const sectionLabel = resolveSectionLabel(heading);

  return (
    <Section aria-label={sectionLabel} className={className}>
      <Outer>
        {heading ? <Heading>{heading}</Heading> : null}
        <List>
          {items.map((item, index) => {
            const itemId = `${baseId}-${index}`;

            return (
              <Item key={`${itemId}-${item.question}`}>
                <Toggle
                  id={itemId}
                  checked={openItemIndex === index}
                  onChange={event => {
                    setOpenItemIndex(
                      event.currentTarget.checked ? index : null,
                    );
                  }}
                  type="checkbox"
                />
                <Summary htmlFor={itemId}>
                  <Question role="heading" aria-level={3}>
                    {item.question}
                  </Question>
                  <IconWrap aria-hidden="true">
                    <PlusIcon viewBox="0 0 14 14" fill="none">
                      <path
                        d="M7 1L7 13M13 7L1 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </PlusIcon>
                    <MinusIcon viewBox="0 0 14 2" fill="none">
                      <path
                        d="M1 1L13.0029 1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </MinusIcon>
                  </IconWrap>
                </Summary>
                <Panel>
                  <PanelInner>
                    <Answer>
                      {item.answer.map((paragraph, paragraphIndex) => (
                        <Paragraph key={`${itemId}-${paragraphIndex}`}>
                          {paragraph}
                        </Paragraph>
                      ))}
                    </Answer>
                  </PanelInner>
                </Panel>
              </Item>
            );
          })}
        </List>
      </Outer>
    </Section>
  );
}
